<template>
  <transition name="modal-fade">
    <div 
      v-if="show" 
      class="detail-modal-backdrop" 
      @click.self="closeModal"
      @keydown.esc="closeModal"
      tabindex="0"
    >
      <div 
        class="detail-modal-container"
        :class="modalSizeClass"
        role="dialog"
        aria-labelledby="modalTitle"
        aria-describedby="modalDescription"
        aria-modal="true"
      >
        <!-- Modal Header -->
        <div class="detail-modal-header">
          <div class="header-content">
            <div class="title-section">
              <i v-if="icon" class="modal-icon" :class="icon"></i>
              <h3 id="modalTitle" class="modal-title">{{ title }}</h3>
            </div>
            <div class="subtitle" v-if="subtitle">{{ subtitle }}</div>
          </div>
          <button
            type="button"
            class="close-button"
            @click="closeModal"
            aria-label="Cerrar modal"
            title="Cerrar"
          >
            <i class="material-icons">close</i>
          </button>
        </div>

        <!-- Modal Body -->
        <div class="detail-modal-body" id="modalDescription">
          <div class="content-wrapper">
            <!-- Slot para contenido personalizado -->
            <slot name="content">
              <!-- Contenido por defecto si se pasa data -->
              <div v-if="data && Object.keys(data).length > 0" class="data-display">
                <div 
                  v-for="(value, key) in data" 
                  :key="key"
                  class="data-row"
                  v-show="shouldShowField(key, value)"
                >
                  <div class="data-label">
                    <i v-if="getFieldIcon(key)" class="field-icon" :class="getFieldIcon(key)"></i>
                    {{ getFieldLabel(key) }}
                  </div>
                  <div class="data-value" :class="getValueClass(value)">
                    {{ formatValue(value, key) }}
                  </div>
                </div>
              </div>
              <!-- Mensaje si no hay datos -->
              <div v-else class="no-data">
                <i class="material-icons">info</i>
                <p>No hay información disponible</p>
              </div>
            </slot>
          </div>
        </div>

        <!-- Modal Footer -->
        <div class="detail-modal-footer" v-if="showFooter">
          <slot name="footer">
            <div class="default-footer">
              <button 
                type="button" 
                class="btn btn-secondary" 
                @click="closeModal"
              >
                <i class="material-icons">close</i>
                Cerrar
              </button>
              <button 
                v-if="showEditButton"
                type="button" 
                class="btn btn-primary" 
                @click="editItem"
              >
                <i class="material-icons">edit</i>
                Editar
              </button>
            </div>
          </slot>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { parseDateValue } from '@/utils/helpers';

export default {
  name: 'DetailModal',
  props: {
    show: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      required: true
    },
    subtitle: {
      type: String,
      default: ''
    },
    icon: {
      type: String,
      default: ''
    },
    data: {
      type: Object,
      default: () => ({})
    },
    size: {
      type: String,
      default: 'medium', // small, medium, large, xlarge
      validator: value => ['small', 'medium', 'large', 'xlarge'].includes(value)
    },
    showFooter: {
      type: Boolean,
      default: true
    },
    showEditButton: {
      type: Boolean,
      default: false
    },
    hiddenFields: {
      type: Array,
      default: () => []
    },
    fieldLabels: {
      type: Object,
      default: () => ({})
    },
    fieldIcons: {
      type: Object,
      default: () => ({})
    }
  },
  computed: {
    modalSizeClass() {
      return `modal-${this.size}`;
    }
  },
  methods: {
    closeModal() {
      this.$emit('close');
    },
    editItem() {
      this.$emit('edit');
    },
    shouldShowField(key, value) {
      // No mostrar campos ocultos
      if (this.hiddenFields.includes(key)) {
        return false;
      }
      
      // No mostrar campos vacíos o nulos
      if (value === null || value === undefined || value === '') {
        return false;
      }
      
      // No mostrar objetos vacíos
      if (typeof value === 'object' && Object.keys(value).length === 0) {
        return false;
      }
      
      return true;
    },
    getFieldLabel(key) {
      return this.fieldLabels[key] || this.formatFieldName(key);
    },
    getFieldIcon(key) {
      return this.fieldIcons[key] || this.getDefaultIcon(key);
    },
    formatFieldName(key) {
      // Convertir camelCase a formato legible
      return key
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, str => str.toUpperCase())
        .trim();
    },
    getDefaultIcon(key) {
      const iconMap = {
        // Información personal
        firstName: 'person',
        lastName: 'person',
        fullName: 'person',
        name: 'person',
        email: 'email',
        phone: 'phone',
        mainPhone: 'phone',
        secondaryPhone: 'phone',
        address: 'home',
        birthDate: 'cake',
        gender: 'wc',
        maritalStatus: 'favorite',
        curp: 'badge',
        rfc: 'business',
        ine: 'credit_card',
        
        // Información de vehículos
        plate: 'confirmation_number',
        vin: 'fingerprint',
        year: 'calendar_today',
        color: 'palette',
        mileage: 'speed',
        brand: 'directions_car',
        carModel: 'model_training',
        category: 'category',
        client: 'person',
        
        // Información general
        id: 'fingerprint',
        createdAt: 'schedule',
        updatedAt: 'update',
        createdBy: 'person_add',
        status: 'check_circle',
        description: 'description',
        notes: 'note',
        amount: 'attach_money',
        price: 'attach_money',
        total: 'attach_money'
      };
      
      return iconMap[key] || 'info';
    },
    formatValue(value, key) {
      if (value === null || value === undefined) {
        return 'No disponible';
      }
      
      // Formatear fechas (incluyendo datetime)
      if (this.isDateFieldKey(key)) {
        if (typeof value === 'string' && !this.isRawDateValue(value)) {
          return value;
        }
        return this.formatDate(value);
      }
      
      // Formatear objetos anidados
      if (typeof value === 'object') {
        // Formatear vehículo específicamente
        if (key === 'vehiculo' && value.brand && value.carModel) {
          const brand = value.brand.name || value.brand;
          const model = value.carModel.name || value.carModel;
          const year = value.year || '';
          const color = value.color || '';
          return `${brand} ${model} ${year} ${color}`.trim();
        }
        
        // Formatear sucursal
        if (key === 'sucursal' && value.name) {
          return value.name;
        }
        
        // Formatear ejecutivo/cliente
        if ((key === 'ejecutivo' || key === 'cliente') && value.firstName && value.lastName) {
          return `${value.firstName} ${value.lastName}`;
        }
        
        // Formatear otros objetos con name
        if (value.name) return value.name;
        if (value.firstName && value.lastName) {
          return `${value.firstName} ${value.lastName}`;
        }
        if (value.email) return value.email;
        
        // Para otros objetos, mostrar información relevante
        if (value.id && Object.keys(value).length > 1) {
          const relevantFields = Object.keys(value).filter(k => 
            k !== 'id' && 
            (typeof value[k] === 'string' || typeof value[k] === 'number') &&
            value[k] !== null && value[k] !== undefined && value[k] !== ''
          );
          if (relevantFields.length > 0) {
            return relevantFields.map(field => `${field}: ${value[field]}`).join(', ');
          }
        }
        
        return JSON.stringify(value);
      }
      
      // Formatear arrays
      if (Array.isArray(value)) {
        return value.length > 0 ? `${value.length} elementos` : 'Sin elementos';
      }
      
      // Formatear números
      if (typeof value === 'number') {
        if (key.includes('phone') || key.includes('Phone')) {
          return value.toString();
        }
        if (key.includes('amount') || key.includes('price') || key.includes('total') || key.includes('Prestamo') || key.includes('Gps')) {
          return new Intl.NumberFormat('es-MX', {
            style: 'currency',
            currency: 'MXN',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }).format(value);
        }
        if (key.includes('Ordinario') || key.includes('Moratorio')) {
          return `${value.toFixed(2)}%`;
        }
        return value.toLocaleString('es-MX');
      }
      
      // Formatear booleanos
      if (typeof value === 'boolean') {
        return value ? 'Sí' : 'No';
      }
      
      return value.toString();
    },
    isDateFieldKey(key) {
      const lowerKey = String(key).toLowerCase();
      return lowerKey.includes('date') ||
        lowerKey.includes('fecha') ||
        lowerKey.includes('firma') ||
        lowerKey.includes('creacion') ||
        lowerKey.includes('modificacion') ||
        String(key).includes('At');
    },
    isRawDateValue(value) {
      return /^\d{4}-\d{2}-\d{2}/.test(value) || value.includes('T') || (value.includes(' ') && value.includes(':'));
    },
    formatDate(dateString) {
      try {
        const date = parseDateValue(dateString);
        
        // Verificar si la fecha es válida
        if (!date) {
          return dateString;
        }
        
        // Determinar si es solo fecha o incluye hora
        const rawDate = typeof dateString === 'string' ? dateString : '';
        const hasTime = rawDate.includes('T') || rawDate.includes(' ') && rawDate.includes(':');
        
        if (hasTime) {
          // Formato con fecha y hora
          return date.toLocaleDateString('es-MX', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
          });
        } else {
          // Solo fecha
          return date.toLocaleDateString('es-MX', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          });
        }
      } catch (error) {
        return dateString;
      }
    },
    getValueClass(value) {
      if (typeof value === 'boolean') {
        return value ? 'value-true' : 'value-false';
      }
      if (typeof value === 'number') {
        return 'value-number';
      }
      return 'value-text';
    },
  },
  watch: {
    show(newVal) {
      if (newVal) {
        // Enfocar el modal cuando se abre
        this.$nextTick(() => {
          const modal = this.$el.querySelector('.detail-modal-backdrop');
          if (modal) {
            modal.focus();
          }
        });
        
        // Prevenir scroll del body
        document.body.style.overflow = 'hidden';
      } else {
        // Restaurar scroll del body
        document.body.style.overflow = '';
      }
    }
  },
  beforeUnmount() {
    // Asegurar que se restaure el scroll del body
    document.body.style.overflow = '';
  }
};
</script>

<style lang="scss" scoped>
// Variables
$modal-backdrop-bg: rgba(0, 0, 0, 0.6);
$modal-bg: #ffffff;
$modal-border-radius: 12px;
$modal-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
$modal-header-bg: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
$modal-header-text: #ffffff;
$modal-border: #e9ecef;
$text-primary: #2c3e50;
$text-secondary: #6c757d;
$text-muted: #adb5bd;
$success-color: #28a745;
$danger-color: #dc3545;
$warning-color: #ffc107;
$info-color: #17a2b8;

// Animaciones
@keyframes modalFadeIn {
  from {
    opacity: 0;
    transform: scale(0.9) translateY(-20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

@keyframes modalFadeOut {
  from {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
  to {
    opacity: 0;
    transform: scale(0.9) translateY(-20px);
  }
}

// Backdrop
.detail-modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: $modal-backdrop-bg;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1050;
  padding: 20px;
  box-sizing: border-box;
  outline: none;
}

// Contenedor del modal
.detail-modal-container {
  background: $modal-bg;
  border-radius: $modal-border-radius;
  box-shadow: $modal-shadow;
  max-height: 90vh;
  width: 100%;
  display: flex;
  flex-direction: column;
  animation: modalFadeIn 0.3s ease-out;
  overflow: hidden;
  
  // Tamaños
  &.modal-small {
    max-width: 400px;
  }
  
  &.modal-medium {
    max-width: 600px;
  }
  
  &.modal-large {
    max-width: 800px;
  }
  
  &.modal-xlarge {
    max-width: 1000px;
  }
}

// Header
.detail-modal-header {
  background: $modal-header-bg;
  color: $modal-header-text;
  padding: 20px 24px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  position: relative;
  
  .header-content {
    flex: 1;
    margin-right: 20px;
  }
  
  .title-section {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 4px;
  }
  
  .modal-icon {
    font-size: 24px;
    opacity: 0.9;
  }
  
  .modal-title {
    font-size: 1.5rem;
    font-weight: 600;
    margin: 0;
    line-height: 1.3;
  }
  
  .subtitle {
    font-size: 0.9rem;
    opacity: 0.8;
    margin-top: 4px;
  }
  
  .close-button {
    background: rgba(255, 255, 255, 0.1);
    border: none;
    border-radius: 50%;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;
    color: $modal-header-text;
    flex-shrink: 0;
    
    &:hover {
      background: rgba(255, 255, 255, 0.2);
      transform: scale(1.05);
    }
    
    &:active {
      transform: scale(0.95);
    }
    
    .material-icons {
      font-size: 20px;
    }
  }
}

// Body
.detail-modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 0;
  
  .content-wrapper {
    padding: 24px;
  }
  
  .data-display {
    .data-row {
      display: flex;
      align-items: flex-start;
      padding: 12px 0;
      border-bottom: 1px solid #f8f9fa;
      
      &:last-child {
        border-bottom: none;
      }
      
      .data-label {
        flex: 0 0 40%;
        font-weight: 600;
        color: $text-primary;
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 0.9rem;
        
        .field-icon {
          font-size: 18px;
          color: $text-secondary;
        }
      }
      
      .data-value {
        flex: 1;
        color: $text-secondary;
        font-size: 0.9rem;
        line-height: 1.4;
        word-break: break-word;
        
        &.value-true {
          color: $success-color;
          font-weight: 500;
        }
        
        &.value-false {
          color: $danger-color;
          font-weight: 500;
        }
        
        &.value-number {
          color: $info-color;
          font-weight: 500;
        }
        
        &.value-text {
          color: $text-secondary;
        }
      }
    }
  }
  
  .no-data {
    text-align: center;
    padding: 40px 20px;
    color: $text-muted;
    
    .material-icons {
      font-size: 48px;
      margin-bottom: 16px;
      opacity: 0.5;
    }
    
    p {
      margin: 0;
      font-size: 1.1rem;
    }
  }
}

// Footer
.detail-modal-footer {
  border-top: 1px solid $modal-border;
  padding: 16px 24px;
  background: #f8f9fa;
  
  .default-footer {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
  }
  
  .btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 20px;
    border: none;
    border-radius: 6px;
    font-size: 0.9rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    text-decoration: none;
    
    .material-icons {
      font-size: 18px;
    }
    
    &.btn-secondary {
      background: #6c757d;
      color: white;
      
      &:hover {
        background: #5a6268;
        transform: translateY(-1px);
      }
    }
    
    &.btn-primary {
      background: #007bff;
      color: white;
      
      &:hover {
        background: #0056b3;
        transform: translateY(-1px);
      }
    }
    
    &:active {
      transform: translateY(0);
    }
  }
}

// Transiciones
.modal-fade-enter-active {
  animation: modalFadeIn 0.3s ease-out;
}

.modal-fade-leave-active {
  animation: modalFadeOut 0.2s ease-in;
}

// Responsive
@media (max-width: 768px) {
  .detail-modal-backdrop {
    padding: 10px;
  }
  
  .detail-modal-container {
    max-height: 95vh;
    
    &.modal-large,
    &.modal-xlarge {
      max-width: 100%;
    }
  }
  
  .detail-modal-header {
    padding: 16px 20px;
    
    .modal-title {
      font-size: 1.3rem;
    }
    
    .close-button {
      width: 36px;
      height: 36px;
      
      .material-icons {
        font-size: 18px;
      }
    }
  }
  
  .detail-modal-body .content-wrapper {
    padding: 20px;
  }
  
  .detail-modal-footer {
    padding: 12px 20px;
    
    .default-footer {
      flex-direction: column;
      
      .btn {
        width: 100%;
        justify-content: center;
      }
    }
  }
  
  .data-display .data-row {
    flex-direction: column;
    gap: 4px;
    
    .data-label {
      flex: none;
      font-size: 0.8rem;
    }
    
    .data-value {
      flex: none;
    }
  }
}

// Scroll personalizado
.detail-modal-body::-webkit-scrollbar {
  width: 6px;
}

.detail-modal-body::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.detail-modal-body::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.detail-modal-body::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>
